import React, { useEffect, useRef, useState, useCallback } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * useSpeechRecognition — Web Speech API wrapper that works reliably across
 * desktop AND mobile browsers.
 *
 * Key insight: on mobile (Chrome Android / Safari iOS) the browser silently
 * restarts the recognizer on every pause when `continuous=true`, which causes
 * already-emitted `final` results to be re-emitted -> duplicated text.
 * Strategy: use `continuous=false`, maintain our own session accumulator, and
 * auto-restart the recognizer on `onend` while the user wants to keep dictating.
 *
 * @param {object} opts
 *   - lang: BCP-47 code (default "es-SV")
 *   - onSessionUpdate: ({ liveText, committedText }) => void
 *      liveText    = base + committed (final pieces) + current interim
 *      committedText = base + committed (final pieces only)
 */
function useSpeechRecognition({ lang = "es-SV", onSessionUpdate } = {}) {
    const recognitionRef = useRef(null);
    const [listening, setListening] = useState(false);
    const [supported, setSupported] = useState(true);
    const onUpdateRef = useRef(onSessionUpdate);
    onUpdateRef.current = onSessionUpdate;

    // Session state — kept in refs so it survives recognizer auto-restarts
    const baseRef = useRef("");           // text before user pressed mic
    const sessionFinalRef = useRef("");   // final chunks accumulated this dictation
    const userListeningRef = useRef(false);  // user-intent (vs internal browser-stop)

    const joinClean = (a, b) => `${a || ""} ${b || ""}`.replace(/\s+/g, " ").trim();

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setSupported(false);
            return;
        }
        const rec = new SpeechRecognition();
        // continuous=false is critical for mobile reliability — we manually restart.
        rec.continuous = false;
        rec.interimResults = true;
        rec.lang = lang;
        rec.maxAlternatives = 1;

        rec.onresult = (event) => {
            let interim = "";
            let final = "";
            // Iterate ALL results — since continuous=false, the list is small and
            // resets between auto-restarts, so we don't risk double-counting.
            for (let i = 0; i < event.results.length; i++) {
                const r = event.results[i];
                if (r.isFinal) final += r[0].transcript;
                else interim += r[0].transcript;
            }
            const committed = joinClean(baseRef.current, sessionFinalRef.current);
            const live = joinClean(joinClean(committed, final), interim);
            if (onUpdateRef.current) {
                onUpdateRef.current({ liveText: live, committedText: committed, hasFinal: !!final, finalPiece: final });
            }
            // Once a piece is "final", lock it in so the next recognizer cycle
            // can't re-emit it.
            if (final) {
                sessionFinalRef.current = joinClean(sessionFinalRef.current, final);
            }
        };

        rec.onerror = (e) => {
            if (e.error === "not-allowed" || e.error === "service-not-allowed") {
                toast.error("Permite el acceso al micrófono para dictar por voz");
                userListeningRef.current = false;
                setListening(false);
            } else if (e.error === "no-speech") {
                // Common on mobile when no speech detected; the recognizer will end
                // and we'll restart from onend.
            } else if (e.error === "audio-capture") {
                toast.error("No se pudo acceder al micrófono");
                userListeningRef.current = false;
                setListening(false);
            } else if (e.error !== "aborted") {
                // Don't spam: surface only unfamiliar errors
                // eslint-disable-next-line no-console
                console.warn("SpeechRecognition error:", e.error);
            }
        };

        rec.onend = () => {
            // The browser internally stops on every pause (mobile especially).
            // While the user is still "listening", restart immediately.
            if (userListeningRef.current) {
                try {
                    rec.start();
                } catch {
                    // start() can throw if called too soon; try once more on next tick
                    setTimeout(() => {
                        if (userListeningRef.current) {
                            try { rec.start(); } catch { /* give up; user will tap mic again */ }
                        }
                    }, 200);
                }
            } else {
                setListening(false);
                // Emit the final committed text one last time so callers can persist it
                const committed = joinClean(baseRef.current, sessionFinalRef.current);
                if (onUpdateRef.current) {
                    onUpdateRef.current({ liveText: committed, committedText: committed, hasFinal: true, finalPiece: "", sessionEnded: true });
                }
            }
        };

        recognitionRef.current = rec;
        return () => {
            userListeningRef.current = false;
            try { rec.stop(); } catch { /* noop */ }
            recognitionRef.current = null;
        };
    }, [lang]);

    const start = useCallback((initialValue = "") => {
        const rec = recognitionRef.current;
        if (!rec) return;
        baseRef.current = initialValue || "";
        sessionFinalRef.current = "";
        userListeningRef.current = true;
        try {
            rec.start();
            setListening(true);
        } catch {
            // already started — toggle listening state anyway
            setListening(true);
        }
    }, []);

    const stop = useCallback(() => {
        userListeningRef.current = false;
        const rec = recognitionRef.current;
        if (!rec) return;
        try { rec.stop(); } catch { /* noop */ }
    }, []);

    return { listening, supported, start, stop };
}

/**
 * VoiceButton - microphone toggle button.
 * value: current text value
 * onChange: receives new text as user speaks
 */
export function VoiceButton({ value, onChange, lang = "es-SV", size = "md", testId, className = "" }) {
    const handleUpdate = useCallback(({ liveText }) => {
        if (typeof liveText === "string") onChange(liveText);
    }, [onChange]);

    const { listening, supported, start, stop } = useSpeechRecognition({
        lang,
        onSessionUpdate: handleUpdate,
    });

    const toggle = () => {
        if (!supported) {
            toast.error("Tu navegador no soporta dictado por voz. Usa Chrome en Android o Safari iOS 14.5+.");
            return;
        }
        if (listening) {
            stop();
        } else {
            start(value || "");
        }
    };

    const sizes = size === "sm" ? "w-8 h-8 rounded-md" : "w-9 h-9 rounded-lg";

    return (
        <button
            type="button"
            onClick={toggle}
            aria-label={listening ? "Detener dictado" : "Dictar por voz"}
            className={`relative inline-flex items-center justify-center ${sizes} transition-colors ${
                listening
                    ? "bg-red-500 text-white animate-pulse"
                    : supported
                        ? "bg-secondary text-foreground hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]"
                        : "bg-secondary text-muted-foreground opacity-60 cursor-not-allowed"
            } ${className}`}
            title={supported ? (listening ? "Detener dictado" : "Dictar por voz") : "No soportado en este navegador"}
            data-testid={testId}
        >
            {supported ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            {listening && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-300 animate-ping" />
            )}
        </button>
    );
}

/**
 * VoiceTextarea - textarea + microphone overlay.
 */
export function VoiceTextarea({ value, onChange, placeholder, className = "", rows = 3, testId, lang = "es-SV", id }) {
    return (
        <div className="relative">
            <textarea
                id={id}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className={`flex w-full rounded-md border border-input bg-background px-3 py-2 pr-12 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
                data-testid={testId}
            />
            <div className="absolute top-2 right-2">
                <VoiceButton value={value} onChange={onChange} lang={lang} size="sm" testId={testId ? `${testId}-mic` : undefined} />
            </div>
        </div>
    );
}

/**
 * VoiceInput - single-line input + microphone overlay.
 */
export function VoiceInput({ value, onChange, placeholder, className = "", testId, lang = "es-SV", id, type = "text" }) {
    return (
        <div className="relative">
            <input
                id={id}
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-11 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
                data-testid={testId}
            />
            <div className="absolute top-1/2 -translate-y-1/2 right-1">
                <VoiceButton value={value} onChange={onChange} lang={lang} size="sm" testId={testId ? `${testId}-mic` : undefined} />
            </div>
        </div>
    );
}

export default VoiceTextarea;
